import React, { memo, useCallback, useEffect, useState } from 'react'
import { Alert, Avatar, Button, Checkbox, Flex, List, Modal, Tag, Typography, theme } from 'antd'
import {
  CloudOutlined,
  DesktopOutlined,
  DollarOutlined,
  GlobalOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  UnlockOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { shortDeviceId } from '@/utils/shiftDevices'

const { Title, Text } = Typography

const RISKS = [
  { id: 'pos.close_shift.force_risk_unsynced', icon: <CloudOutlined /> },
  { id: 'pos.close_shift.force_risk_rejected', icon: <SyncOutlined /> },
  { id: 'pos.close_shift.force_risk_recap', icon: <DollarOutlined /> },
]

/**
 * Modal blokir tutup shift: masih ada device lain yang membuka shift yang sama
 * (multi-device). Langkah 1 menampilkan device (nama & ID pendek, karena nama bisa kembar);
 * bila device lain tak bisa ditutup (mati/hilang/terpasang ulang), kasir bisa "Tutup Paksa"
 * lewat langkah 2 yang menjelaskan risikonya dan wajib dicentang.
 * Sama dengan kledopos-mobile `OpenDevicesBlockModal`.
 */
const OpenDevicesBlockModal = ({ open, devices = [], currentDeviceId, onClose, onForceClose }) => {
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()
  const [step, setStep] = useState('blocked')
  const [acknowledged, setAcknowledged] = useState(false)

  useEffect(() => {
    if (open) {
      setStep('blocked')
      setAcknowledged(false)
    }
  }, [open])

  const goToConfirm = useCallback(() => setStep('confirm'), [])

  const backToBlocked = useCallback(() => {
    setStep('blocked')
    setAcknowledged(false)
  }, [])

  const toggleAcknowledged = useCallback((e) => setAcknowledged(e.target.checked), [])

  const idLabel = useCallback(
    (id) => `${formatMessage({ id: 'pos.close_shift.device_id' })} · ${shortDeviceId(id)}`,
    [formatMessage],
  )

  const isConfirm = step === 'confirm'

  const renderIcon = (icon, color, background) => (
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background,
        color,
        fontSize: 30,
        marginBottom: 16,
      }}
    >
      {icon}
    </div>
  )

  return (
    <Modal
      open={open}
      onCancel={isConfirm ? backToBlocked : onClose}
      footer={null}
      width={480}
      centered
      destroyOnHidden
    >
      {isConfirm ? (
        <>
          <Flex vertical align="center" style={{ textAlign: 'center', paddingTop: 8 }}>
            {renderIcon(<UnlockOutlined />, token.colorError, token.colorErrorBg)}
            <Title level={4} style={{ margin: 0 }}>
              {formatMessage({ id: 'pos.close_shift.force_title' })}
            </Title>
            <Text type="secondary" style={{ marginTop: 6 }}>
              {formatMessage({ id: 'pos.close_shift.force_subtitle' })}
            </Text>
          </Flex>
          <div
            style={{
              marginTop: 20,
              padding: '4px 16px',
              borderRadius: token.borderRadiusLG,
              background: token.colorErrorBg,
            }}
          >
            {RISKS.map((risk, index) => (
              <Flex
                key={risk.id}
                gap={12}
                align="flex-start"
                style={{
                  padding: '12px 0',
                  borderTop: index > 0 ? `1px solid ${token.colorErrorBorder}` : undefined,
                }}
              >
                <span style={{ color: token.colorError, fontSize: 16, lineHeight: '22px' }}>
                  {risk.icon}
                </span>
                <Text>{formatMessage({ id: risk.id })}</Text>
              </Flex>
            ))}
          </div>
          <Alert
            type="warning"
            showIcon
            icon={<ThunderboltOutlined />}
            style={{ marginTop: 12 }}
            message={formatMessage({ id: 'pos.close_shift.force_zero_loading_title' })}
            description={formatMessage({ id: 'pos.close_shift.force_zero_loading_desc' })}
          />
          <Checkbox checked={acknowledged} onChange={toggleAcknowledged} style={{ marginTop: 16 }}>
            {formatMessage({ id: 'pos.close_shift.force_acknowledge' })}
          </Checkbox>
          <Button
            type="primary"
            danger
            block
            size="large"
            style={{ marginTop: 20 }}
            disabled={!acknowledged}
            onClick={onForceClose}
          >
            {formatMessage({ id: 'pos.close_shift.force_confirm' })}
          </Button>
          <Button type="text" block style={{ marginTop: 8 }} onClick={backToBlocked}>
            {formatMessage({ id: 'button.back' })}
          </Button>
        </>
      ) : (
        <>
          <Flex vertical align="center" style={{ textAlign: 'center', paddingTop: 8 }}>
            {renderIcon(<WarningFilled />, token.colorWarning, token.colorWarningBg)}
            <Title level={4} style={{ margin: 0 }}>
              {formatMessage({ id: 'pos.close_shift.devices_title' })}
            </Title>
            <Text type="secondary" style={{ marginTop: 6 }}>
              {formatMessage({ id: 'pos.close_shift.devices_desc' })}
            </Text>
          </Flex>
          <List
            style={{ marginTop: 20 }}
            bordered
            dataSource={devices}
            rowKey={(device, index) => `${device.device_id}-${index}`}
            renderItem={(device) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <Avatar
                      icon={<DesktopOutlined />}
                      style={{
                        background: token.colorFillSecondary,
                        color: token.colorTextSecondary,
                      }}
                    />
                  }
                  title={device.nickname || formatMessage({ id: 'pos.close_shift.unnamed_device' })}
                  description={
                    <Flex vertical>
                      {device.device_info ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {device.device_info}
                        </Text>
                      ) : null}
                      <Text type="secondary" style={{ fontSize: 12, letterSpacing: 0.5 }}>
                        {idLabel(device.device_id)}
                      </Text>
                    </Flex>
                  }
                />
              </List.Item>
            )}
          />
          {currentDeviceId ? (
            <Flex
              align="center"
              gap={12}
              style={{
                marginTop: 8,
                padding: '10px 24px',
                border: `1px solid ${token.colorPrimaryBorder}`,
                borderRadius: token.borderRadiusLG,
              }}
            >
              <Avatar
                icon={<GlobalOutlined />}
                style={{ background: token.colorPrimaryBg, color: token.colorPrimary }}
              />
              <Text type="secondary" style={{ flex: 1, fontSize: 12, letterSpacing: 0.5 }}>
                {idLabel(currentDeviceId)}
              </Text>
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {formatMessage({ id: 'pos.close_shift.this_browser' })}
              </Tag>
            </Flex>
          ) : null}
          <Button type="primary" block size="large" style={{ marginTop: 20 }} onClick={onClose}>
            {formatMessage({ id: 'button.close' })}
          </Button>
          <Button
            type="text"
            danger
            block
            icon={<UnlockOutlined />}
            style={{ marginTop: 8 }}
            onClick={goToConfirm}
          >
            {formatMessage({ id: 'pos.close_shift.force_close' })}
          </Button>
        </>
      )}
    </Modal>
  )
}

export default memo(OpenDevicesBlockModal)
